/**
 * The viewer.
 *
 * Nothing here authors geometry. It loads LevelSpec JSON, runs the same
 * compiler and the same validators the CLI runs, and puts you inside the
 * result — next to a switch that rebuilds the identical spec the naive way so
 * the difference is something you can walk up to and look at.
 */

import * as THREE from 'three';

import { bakeNavmesh, checkGeometry, compile, compileNaive, toCadQuery, toDXF, toOBJ, toOpenSCAD, toQuakeMap, validate, type GeometryReport, type NavmeshReport, type ValidationReport } from 'levelspec';
import type { CompiledLevel, LevelSpec, Solid } from 'levelspec';

import { DYNAMIC_ROLES, ROLE_STYLE, fromThree, toThree } from '../three/adapter.ts';
import { buildEnvironment, buildLevelView, buildNavmeshOverlay, type LevelView } from './scene.ts';
import { Player } from '../three/player.ts';
import { drawPlan, layerAt } from './minimap.ts';
import { Game } from './game.ts';

// ---------------------------------------------------------------------------
// Level catalogue
// ---------------------------------------------------------------------------

/*
 * The specs live in the compiler's own repo, next to the compiler.
 *
 * This used to glob `../levels`, which is a directory in this package that
 * does not exist — so the catalogue came back empty, the first load was
 * `undefined`, and the page hung on "Compiling…" for ever. Pointed at the
 * sibling checkout, the same way the map library is.
 */
const modules = import.meta.glob('../../../levelspec/levels/*.json', { eager: true, import: 'default' });
const SPECS: LevelSpec[] = Object.values(modules) as LevelSpec[];
if (!SPECS.length) {
  // Loud, rather than a spinner that never stops.
  throw new Error('No level specs found: expected packages/levelspec/levels/*.json');
}
// The tutorial map first, the deliberately broken one last, the rest by name.
const rank = (s: LevelSpec): number =>
  s.id === 'compiler_demo_hall' ? 0 : s.expect_fail ? 2 : 1;
SPECS.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));

interface Built {
  spec: LevelSpec;
  level: CompiledLevel;
  report: ValidationReport;
  naiveSolids: Solid[];
  naiveGeom: GeometryReport;
  compiledView: LevelView;
  naiveView: LevelView | null;
  compileMs: number;
  /** Baked on first use — it is the expensive one. */
  mesh?: NavmeshReport;
  meshOverlay?: THREE.Points;
}

const cache = new Map<string, Built>();
const chipInfo = new Map<string, { passed: boolean; expectFail: boolean; solids: number }>();

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const viewport = document.getElementById('viewport') as HTMLDivElement;
const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setSize(innerWidth, innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
viewport.appendChild(renderer.domElement);

const scene = new THREE.Scene();
buildEnvironment(scene);
const camera = new THREE.PerspectiveCamera(78, innerWidth / innerHeight, 0.05, 1200);
const player = new Player(camera);
const game = new Game(scene, player);

addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(innerWidth, innerHeight);
});

// ---------------------------------------------------------------------------
// View state
// ---------------------------------------------------------------------------

type Mode = 'compiled' | 'naive';

const state = {
  current: null as Built | null,
  mode: 'compiled' as Mode,
  showNav: false,
  showBoundaries: false,
  showLabels: false,
  showDefects: false,
  showMarkers: true,
  showNavmesh: false,
  hideRoofs: true,
  breached: false,
  isolatedLayer: null as string | null,
  reportTab: 'summary',
  markerIndex: 0,
  playing: false,
};

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * Yield a frame — but never wait forever. A backgrounded tab can stop
 * servicing requestAnimationFrame entirely, and a viewer that never finishes
 * booting because it was loaded in an inactive tab is not acceptable.
 */
const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    requestAnimationFrame(finish);
    setTimeout(finish, 80);
  });

async function build(spec: LevelSpec): Promise<Built> {
  const hit = cache.get(spec.id);
  if (hit) return hit;

  const t0 = performance.now();
  const level = compile(spec);
  const report = validate(level);
  const compileMs = performance.now() - t0;

  const naiveSolids = compileNaive(spec).solids;
  const naiveGeom = checkGeometry(naiveSolids);

  const built: Built = {
    spec,
    level,
    report,
    naiveSolids,
    naiveGeom,
    compiledView: buildLevelView(level, level.solids, report.geometry.offender_ids),
    naiveView: null,
    compileMs,
  };
  cache.set(spec.id, built);
  chipInfo.set(spec.id, {
    passed: report.passed,
    expectFail: spec.expect_fail === true,
    solids: report.geometry.solids,
  });
  return built;
}

function naiveView(b: Built): LevelView {
  if (!b.naiveView) b.naiveView = buildLevelView(b.level, b.naiveSolids, b.naiveGeom.offender_ids);
  return b.naiveView;
}

async function loadLevel(spec: LevelSpec): Promise<void> {
  if (state.playing) stopPlay();
  el('loading').classList.remove('hidden');
  el<HTMLSpanElement>('loading-text').textContent = `Compiling ${spec.name}…`;
  await nextFrame();
  await nextFrame();

  if (state.current) {
    scene.remove(state.current.compiledView.group);
    if (state.current.naiveView) scene.remove(state.current.naiveView.group);
    removeOverlays(state.current.compiledView);
    if (state.current.naiveView) removeOverlays(state.current.naiveView);
    if (state.current.meshOverlay) scene.remove(state.current.meshOverlay);
  }

  const built = await build(spec);
  state.current = built;
  state.isolatedLayer = null;
  state.markerIndex = 0;
  applyMode();
  player.killPlane = built.compiledView.bounds.min.y - 12;
  overview(built);
  renderRail();
  renderReport();
  renderControls();
  el('loading').classList.add('hidden');
}

function removeOverlays(view: LevelView): void {
  for (const o of Object.values(view.overlays)) scene.remove(o);
}

function addOverlays(view: LevelView): void {
  for (const o of Object.values(view.overlays)) scene.add(o);
}

function activeView(): LevelView {
  const b = state.current!;
  return state.mode === 'compiled' ? b.compiledView : naiveView(b);
}

function applyMode(): void {
  const b = state.current!;
  const active = activeView();
  const other = state.mode === 'compiled' ? b.naiveView : b.compiledView;
  if (other) {
    scene.remove(other.group);
    removeOverlays(other);
  }
  scene.add(active.group);
  addOverlays(active);
  applyToggles();
  updateModeStats();
}

/** Bake the navmesh on demand: it is the one derivation worth waiting for. */
function navmeshOverlay(b: Built): THREE.Points {
  if (!b.meshOverlay) {
    b.mesh = bakeNavmesh(b.level, { keepSamples: true });
    b.meshOverlay = buildNavmeshOverlay(b.mesh);
  }
  if (!b.meshOverlay.parent) scene.add(b.meshOverlay);
  return b.meshOverlay;
}

function applyToggles(): void {
  const view = activeView();
  view.overlays.nav.visible = state.showNav;
  view.overlays.boundaries.visible = state.showBoundaries;
  view.overlays.labels.visible = state.showLabels;
  view.overlays.defects.visible = state.showDefects;
  view.overlays.markers.visible = state.showMarkers;

  for (const [layerId, meshes] of view.byLayer)
    for (const m of meshes) m.visible = state.isolatedLayer === null || state.isolatedLayer === layerId;

  if (state.hideRoofs) for (const m of view.byRole.get('roof') ?? []) m.visible = false;

  if (state.breached)
    for (const role of DYNAMIC_ROLES) for (const m of view.byRole.get(role) ?? []) m.visible = false;

  const b = state.current;
  if (b) {
    if (state.showNavmesh && state.mode === 'compiled') navmeshOverlay(b).visible = true;
    else if (b.meshOverlay) b.meshOverlay.visible = false;
  }

  player.ignore = state.breached ? new Set(view.dynamicIds) : new Set();
  player.setColliders(view.colliders);
}

/** Drop in at a spawn marker, on foot. */
function spawnPlayer(b: Built): void {
  const spawn = b.level.markers.find((m) => m.kind === 'attacker_spawn') ?? b.level.markers[0] ?? null;
  if (spawn) {
    const p = toThree(spawn.pos[0], spawn.pos[1], spawn.pos[2] + 0.05);
    player.teleport(p);
    const c = b.compiledView.bounds.getCenter(new THREE.Vector3());
    player.yaw = Math.atan2(-(c.x - p.x), -(c.z - p.z));
  } else {
    const c = b.compiledView.bounds.getCenter(new THREE.Vector3());
    player.teleport(new THREE.Vector3(c.x, b.compiledView.bounds.min.y + 2, c.z));
  }
  player.pitch = 0;
  player.mode = 'walk';
}

/** Open on a three-quarter view of the whole level, already flying. */
function overview(b: Built): void {
  const box = b.compiledView.bounds;
  const c = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const reach = Math.max(size.x, size.z) * 0.95 + 18;
  player.teleport(new THREE.Vector3(c.x - reach * 0.62, box.max.y + reach * 0.5, c.z + reach * 0.62));
  // Aim from the new eye position, not the camera's previous one — the camera
  // does not catch up until the next frame.
  const eye = new THREE.Vector3(player.pos.x, player.pos.y + 1.62, player.pos.z);
  const dir = c.clone().sub(eye);
  player.yaw = Math.atan2(-dir.x, -dir.z);
  player.pitch = Math.atan2(dir.y, Math.hypot(dir.x, dir.z));
  player.mode = 'fly';
}

// ---------------------------------------------------------------------------
// UI — level rail
// ---------------------------------------------------------------------------

function renderRail(): void {
  const list = el('level-list');
  list.innerHTML = '';
  for (const spec of SPECS) {
    const info = chipInfo.get(spec.id);
    const btn = document.createElement('button');
    btn.className = 'level-item' + (state.current?.spec.id === spec.id ? ' active' : '');
    const chip = info
      ? info.expectFail
        ? `<span class="chip intent">by design</span>`
        : info.passed
          ? `<span class="chip pass">pass</span>`
          : `<span class="chip fail">fail</span>`
      : `<span class="chip" style="color:var(--dim)">…</span>`;
    const layers = spec.layers.length;
    const spaces = spec.layers.reduce((a, l) => a + l.spaces.length, 0);
    btn.innerHTML =
      `<div class="li-top"><span class="li-name">${spec.name}</span>${chip}</div>` +
      `<div class="li-meta">${layers} storey${layers === 1 ? '' : 's'} · ${spaces} spaces` +
      (info ? ` · ${info.solids} solids` : '') +
      `</div>`;
    btn.onclick = () => void loadLevel(spec);
    list.appendChild(btn);
  }
}

// ---------------------------------------------------------------------------
// UI — top bar
// ---------------------------------------------------------------------------

function updateModeStats(): void {
  const b = state.current;
  if (!b) return;
  const g = state.mode === 'compiled' ? b.report.geometry : b.naiveGeom;
  const bad = (n: number): string => (n === 0 ? `<b class="ok">0</b>` : `<b class="bad">${n}</b>`);
  el('mode-stats').innerHTML =
    `<b>${g.solids}</b> solids · <b>${g.triangles.toLocaleString()}</b> tris · ` +
    `${bad(g.zfight_pairs)} coplanar-same-normal · ${bad(g.intersecting_pairs)} intersecting · ` +
    `brush <b>${g.brush_tests_passed}/${g.brush_tests_total}</b> · winding <b>${g.winding_ok ? 'ok' : 'bad'}</b>`;

  const banner = el('naive-banner');
  if (state.mode === 'naive') {
    banner.classList.remove('hidden');
    banner.innerHTML =
      `<b>Naive build of the same spec.</b> Every box here is individually watertight, correctly wound, ` +
      `and passes its half-space test — and the collection still has <b>${b.naiveGeom.zfight_pairs}</b> coplanar ` +
      `face pairs and <b>${b.naiveGeom.intersecting_pairs}</b> overlapping volumes. The doors were never cut, so ` +
      `press <kbd>F</kbd> to fly through. <kbd>X</kbd> paints the defects red.`;
  } else {
    banner.classList.add('hidden');
  }
  for (const btn of document.querySelectorAll<HTMLButtonElement>('#mode-switch button'))
    if (btn.dataset.mode) btn.classList.toggle('active', btn.dataset.mode === state.mode && !state.playing);
  el('play-btn').classList.toggle('active', state.playing);
}

for (const btn of document.querySelectorAll<HTMLButtonElement>('#mode-switch button')) {
  btn.onclick = () => {
    state.mode = btn.dataset.mode as Mode;
    applyMode();
    renderReport();
  };
}

// ---------------------------------------------------------------------------
// UI — report
// ---------------------------------------------------------------------------

for (const btn of document.querySelectorAll<HTMLButtonElement>('#report-tabs button')) {
  btn.onclick = () => {
    state.reportTab = btn.dataset.tab!;
    for (const b2 of document.querySelectorAll<HTMLButtonElement>('#report-tabs button'))
      b2.classList.toggle('active', b2.dataset.tab === state.reportTab);
    renderReport();
  };
}

const kv = (k: string, v: string | number, cls = ''): string =>
  `<div class="kv"><span>${k}</span><span class="${cls}">${v}</span></div>`;

function renderReport(): void {
  const b = state.current;
  const body = el('report-body');
  if (!b) {
    body.innerHTML = '';
    return;
  }
  if (state.reportTab === 'summary') body.innerHTML = summaryHTML(b);
  else if (state.reportTab === 'diagnostics') body.innerHTML = diagnosticsHTML(b);
  else if (state.reportTab === 'legend') body.innerHTML = legendHTML();
  else body.innerHTML = specHTML(b);

  if (state.reportTab === 'spec') wireDownloads(b);
}

function summaryHTML(b: Built): string {
  const r = b.report;
  const g = state.mode === 'compiled' ? r.geometry : b.naiveGeom;
  const n = r.navigation;
  const t = r.tactical;
  const flag = (v: number): string => (v === 0 ? 'good' : 'bad');

  let html = `<p class="note">${b.spec.description ?? ''}</p>`;

  html += `<div class="sec"><h3>Geometry — ${state.mode}</h3>`;
  html += kv('Solids', g.solids);
  html += kv('Triangles', g.triangles.toLocaleString());
  html += kv('Coplanar, same normal', g.zfight_pairs, flag(g.zfight_pairs));
  html += kv('Intersecting volumes', g.intersecting_pairs, flag(g.intersecting_pairs));
  html += kv('Clean face contacts', g.touching_pairs);
  html += kv('Degenerate solids', g.degenerate, flag(g.degenerate));
  html += kv('Winding consistent', g.winding_ok ? 'yes' : 'no', g.winding_ok ? 'good' : 'bad');
  html += kv(
    'Brush half-space tests',
    `${g.brush_tests_passed}/${g.brush_tests_total}`,
    g.brush_tests_passed === g.brush_tests_total ? 'good' : 'bad',
  );
  html += kv('Runtime ready', g.runtime_ready ? 'yes' : 'no', g.runtime_ready ? 'good' : 'bad');
  html += `</div>`;

  html += `<div class="sec"><h3>Compiler</h3>`;
  html += kv('Boundary runs', b.level.stats.boundaries);
  html += kv('Openings cut', b.level.stats.openings);
  html += kv('Dynamic surfaces', b.level.stats.dynamic_solids);
  html += kv('Vertical links', b.level.stats.verticals);
  html += kv('Compile + validate', `${b.compileMs.toFixed(0)} ms`);
  html += `</div>`;

  html += `<div class="sec"><h3>Navigation</h3>`;
  html += kv('Nodes / edges', `${n.nodes.toLocaleString()} / ${n.edges.toLocaleString()}`);
  html += kv('Components', n.components, n.components === 1 ? 'good' : 'bad');
  html += kv('Narrowest opening', `${n.min_opening_width.toFixed(2)} m`);
  html += kv('Max step rise', `${n.max_step.toFixed(3)} m`, n.step_ok ? 'good' : 'bad');
  html += kv('Min tread', `${n.min_tread.toFixed(2)} m`, n.min_tread >= 0.22 ? 'good' : 'bad');
  html += `</div>`;

  if (n.routes.length) {
    html += `<div class="sec"><h3>Required routes</h3>`;
    for (const rt of n.routes) {
      const val = rt.reachable ? `${rt.distance.toFixed(0)} m · ${rt.seconds.toFixed(1)} s` : 'unreachable';
      html +=
        `<div class="route"><span class="rid">${rt.id}</span>` +
        `<span class="rv ${rt.ok ? 'good' : 'bad'}" style="color:var(--${rt.ok ? 'ok' : 'bad'})">${val}</span>` +
        `<span class="rv" style="color:var(--dim)">${rt.disjoint_routes}×</span></div>`;
      if (rt.note) html += `<div style="color:var(--warn);font-size:10.5px;padding:0 0 4px">${rt.note}</div>`;
    }
    html += `</div>`;
  }

  html += `<div class="sec"><h3>Navmesh — baked from the geometry</h3>`;
  if (!b.mesh) {
    html += `<p class="note">A second derivation, from the compiled solids rather than the spec: walkable surfaces for a ${(b.spec.player?.radius ?? 0.35) * 2} m agent, connected by walking and stepping. Press <b>J</b> to bake and draw it.</p>`;
  } else {
    const m = b.mesh;
    html += kv('Samples @ ' + m.cell + ' m', m.samples.toLocaleString());
    html += kv('On playable floor', m.playable_samples.toLocaleString());
    html += kv(
      'Reachable from spawn',
      `${(m.playable_fraction * 100).toFixed(1)}%`,
      m.playable_fraction === 1 ? 'good' : 'bad',
    );
    html += kv('Unreachable spaces', m.unreachable_spaces.length, m.unreachable_spaces.length ? 'bad' : 'good');
    html += kv('Playable islands', m.playable_islands, m.playable_islands ? 'bad' : 'good');
    html += kv('Off-mesh ledges', m.islands.length - m.playable_islands);
    html += kv('Markers off navmesh', m.unreachable_markers.length, m.unreachable_markers.length ? 'bad' : 'good');
    html += kv('Bake time', `${m.ms.toFixed(0)} ms`);
    if (m.unreachable_spaces.length)
      html += `<p class="note" style="color:var(--bad)">Cannot walk to: ${m.unreachable_spaces.join(', ')}</p>`;
    html += `<p class="note">Green is reachable, red is an island inside a declared space, grey is a roof or ledge that was never meant to be walked.</p>`;
  }
  html += `</div>`;

  html += `<div class="sec"><h3>Tactical</h3>`;
  html += kv('Longest sightline', `${t.longest_sightline.toFixed(1)} m`);
  html += kv('p95 / median sightline', `${t.p95_sightline.toFixed(1)} / ${t.median_sightline.toFixed(1)} m`);
  html += kv('Sightline samples', t.samples.toLocaleString());
  html += kv('Chokepoints (≤1.5 m)', t.chokepoints);
  html += kv('Soft walls', t.soft_walls);
  html += kv('Reinforcement slots', t.reinforcement_slots);
  html += kv('Hatches', t.hatches);
  html += kv('Rappel surfaces', t.rappel_surfaces);
  html += kv('Spawn LOS violations', t.spawn_los_violations.length, flag(t.spawn_los_violations.length));
  html += `</div>`;

  html += `<div class="sec"><h3>Same spec, built naively</h3>`;
  html += `<p class="note">The control condition: one loop, four walls, a floor and a ceiling per room, and a door panel nudged in front of the wall it never cut.</p>`;
  html += kv('Coplanar, same normal', b.naiveGeom.zfight_pairs, 'bad');
  html += kv('Intersecting volumes', b.naiveGeom.intersecting_pairs, 'bad');
  html += kv('Every box watertight', 'yes', 'good');
  html += kv('Every box wound right', b.naiveGeom.winding_ok ? 'yes' : 'no', 'good');
  html += kv('Half-space tests', `${b.naiveGeom.brush_tests_passed}/${b.naiveGeom.brush_tests_total}`, 'good');
  html += `</div>`;

  return html;
}

function diagnosticsHTML(b: Built): string {
  const ds = b.report.diagnostics;
  if (!ds.length)
    return `<p class="note">No diagnostics. Every hard gate passed: geometry, movement clearance, connectivity, route budgets and spawn sightlines.</p>`;
  let html = `<p class="note">${ds.length} finding(s). Each carries a stable code, the objects involved and measured values — this is the payload that goes back to whatever authored the spec.</p>`;
  for (const d of ds) {
    html += `<div class="diag ${d.severity}"><div class="code">${d.code}</div>`;
    html += `<div class="msg">${d.message}</div>`;
    if (d.objects.length) html += `<div class="objs">${d.objects.join(' · ')}</div>`;
    if (d.measured !== undefined)
      html += `<div class="objs">measured ${d.measured}${d.required !== undefined ? ` · required ${d.required}` : ''}</div>`;
    if (d.suggestions?.length) html += `<ul>${d.suggestions.map((s) => `<li>${s}</li>`).join('')}</ul>`;
    html += `</div>`;
  }
  return html;
}

function legendHTML(): string {
  let html = `<div class="sec"><h3>Surface roles</h3>`;
  for (const [role, style] of Object.entries(ROLE_STYLE)) {
    const hex = `#${style.color.toString(16).padStart(6, '0')}`;
    html += `<div class="legend-row"><i class="sw" style="background:${hex}"></i><span><b>${style.label}</b> <span style="color:var(--dim)">${role}</span></span></div>`;
  }
  html += `</div><div class="sec"><h3>Overlays</h3>`;
  html += `<div class="legend-row"><i class="sw" style="background:#3fb27f"></i><span>Walk edge</span></div>`;
  html += `<div class="legend-row"><i class="sw" style="background:#ffd75e"></i><span>Through an opening</span></div>`;
  html += `<div class="legend-row"><i class="sw" style="background:#ff7a5a"></i><span>Vertical link</span></div>`;
  html += `<div class="legend-row"><i class="sw" style="background:#ff3355"></i><span>Geometry defect</span></div>`;
  html += `</div><div class="sec"><h3>Markers</h3>`;
  html += `<div class="legend-row"><i class="sw" style="background:#ff6b5a"></i><span>Attacker spawn</span></div>`;
  html += `<div class="legend-row"><i class="sw" style="background:#62a8ff"></i><span>Defender spawn</span></div>`;
  html += `<div class="legend-row"><i class="sw" style="background:#ffd75e"></i><span>Site</span></div>`;
  html += `<div class="legend-row"><i class="sw" style="background:#a78bfa"></i><span>Point of interest</span></div>`;
  html += `</div>`;
  return html;
}

function specHTML(b: Built): string {
  const json = JSON.stringify(b.spec, null, 2);
  const preview = json.length > 14000 ? json.slice(0, 14000) + '\n… truncated, download for the full spec' : json;
  return (
    `<div class="sec"><h3>Compilation targets</h3>` +
    `<p class="note">Generated from this spec, in the browser, by the same exporters the CLI uses. ` +
    `<code>npm run compile</code> writes the same files to <code>generated/</code>.</p>` +
    `<a class="dl" data-export="map">↓ ${b.spec.id}.map — Quake brushes (half-space tested)</a>` +
    `<a class="dl" data-export="obj">↓ ${b.spec.id}.obj — Wavefront mesh, Z-up</a>` +
    `<a class="dl" data-export="scad">↓ ${b.spec.id}.scad — OpenSCAD Boolean bake</a>` +
    `<a class="dl" data-export="py">↓ ${b.spec.id}.cadquery.py — exact B-rep / STEP</a>` +
    `<a class="dl" data-export="dxf">↓ ${b.spec.id}.dxf — 2D plan</a>` +
    `<a class="dl" data-export="json">↓ ${b.spec.id}.json — the LevelSpec itself</a>` +
    `</div><div class="sec"><h3>LevelSpec source</h3><pre class="spec">${escapeHTML(preview)}</pre></div>`
  );
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!);
}

function wireDownloads(b: Built): void {
  for (const a of document.querySelectorAll<HTMLAnchorElement>('#report-body .dl')) {
    a.onclick = (e) => {
      e.preventDefault();
      const kind = a.dataset.export!;
      const map: Record<string, [string, string]> = {
        map: [toQuakeMap(b.level).text, `${b.spec.id}.map`],
        obj: [toOBJ(b.level), `${b.spec.id}.obj`],
        scad: [toOpenSCAD(b.level), `${b.spec.id}.scad`],
        py: [toCadQuery(b.level), `${b.spec.id}.cadquery.py`],
        dxf: [toDXF(b.level), `${b.spec.id}.dxf`],
        json: [JSON.stringify(b.spec, null, 2), `${b.spec.id}.json`],
      };
      const [text, name] = map[kind];
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    };
  }
}

// ---------------------------------------------------------------------------
// UI — controls list
// ---------------------------------------------------------------------------

function renderControls(): void {
  if (state.playing) {
    el('ctl-grid').innerHTML = (
      [
        ['W A S D', 'Move — A strafes left, D right'],
        ['Mouse', 'Look'],
        ['Left click', 'Shoot (hold to keep firing)'],
        ['R', 'Reload'],
        ['Shift', 'Sprint'],
        ['Space', 'Jump'],
        ['C', 'Crouch'],
        ['M', 'Floor plans'],
        ['Enter', 'Restart wave 1'],
        ['P', 'Back to exploring'],
      ] as [string, string][]
    )
      .map(([k, label]) => `<kbd>${k}</kbd><span>${label}</span>`)
      .join('');
    return;
  }
  const rows: [string, string, boolean?][] = [
    ['WASD', 'Move'],
    ['Mouse', 'Look'],
    ['Shift', 'Sprint / fast fly'],
    ['E / Space', player.mode === 'fly' ? 'Rise' : 'Jump'],
    ['Q / C', player.mode === 'fly' ? 'Descend' : 'Crouch'],
    ['F', `Noclip fly`, player.mode === 'fly'],
    ['V', 'Compiled ↔ naive', state.mode === 'naive'],
    ['N', 'Navigation graph', state.showNav],
    ['B', 'Boundary runs', state.showBoundaries],
    ['L', 'Room labels', state.showLabels],
    ['X', 'Geometry defects', state.showDefects],
    ['K', 'Gameplay markers', state.showMarkers],
    ['J', 'Baked navmesh', state.showNavmesh],
    ['H', 'Hide roofs', state.hideRoofs],
    ['G', 'Breach dynamic surfaces', state.breached],
    ['P', 'Isolate storey', state.isolatedLayer !== null],
    ['M', 'All floor plans'],
    ['Enter', 'Play the level (wave survival)'],
    ['T', 'Teleport to next marker'],
    ['R', 'Drop in at spawn'],
    ['O', 'Back to overview'],
  ];
  el('ctl-grid').innerHTML = rows
    .map(([k, label, on]) => `<kbd>${k}</kbd><span class="${on ? 'on' : ''}">${label}${on ? ' ●' : ''}</span>`)
    .join('');
}

// ---------------------------------------------------------------------------
// Play mode
// ---------------------------------------------------------------------------

function startPlay(): void {
  const b = state.current;
  if (!b) return;
  // Play always runs the breached state, so what the player can walk through
  // and what the enemies path on are the same set of solids. A level whose
  // front door is boarded is not a level you can playtest.
  state.mode = 'compiled';
  state.breached = true;
  state.showNavmesh = false;
  applyMode();

  const view = b.compiledView;
  const spawn =
    b.level.markers.find((m) => m.kind === 'attacker_spawn') ?? b.level.markers[0] ?? null;
  const at = spawn
    ? toThree(spawn.pos[0], spawn.pos[1], spawn.pos[2] + 0.05)
    : view.bounds.getCenter(new THREE.Vector3()).setY(view.bounds.min.y + 2);

  const ok = game.start(b.level, view.colliders.filter((c) => !c.dynamic), at);
  if (!ok) {
    el('hud').textContent = 'This level has no navmesh to play on.';
    return;
  }
  state.playing = true;
  document.body.classList.add('playing');
  el('game-hud').classList.remove('hidden');
  el('game-over').classList.add('hidden');
  renderControls();
  beginLook();
}

function stopPlay(): void {
  game.stop();
  el('play-btn').classList.remove('active');
  state.playing = false;
  state.breached = false;
  document.body.classList.remove('playing');
  el('game-hud').classList.add('hidden');
  applyToggles();
  renderControls();
}

function togglePlay(): void {
  if (state.playing) stopPlay();
  else startPlay();
}

el<HTMLButtonElement>('play-btn').onclick = () => togglePlay();

function renderGameHud(): void {
  const g = game.state;
  el('g-wave').textContent = g.wave > 0 ? `Wave ${g.wave}` : 'Get ready';
  el('g-enemies').textContent = `${g.enemiesRemainingInWave} left`;
  el('g-score').textContent = String(g.score);
  el('g-message').textContent =
    g.intermission > 0 && g.alive ? `${g.message} — ${Math.ceil(g.intermission)}` : g.alive ? '' : '';
  const pct = Math.max(0, g.health) / g.maxHealth;
  (el('g-health-fill') as HTMLElement).style.width = `${pct * 100}%`;
  el('g-health-num').textContent = String(Math.ceil(Math.max(0, g.health)));
  el('g-ammo').textContent = String(g.ammo);
  el('g-reload').classList.toggle('hidden', !g.reloading);
  (el('hit-marker') as HTMLElement).style.opacity = String(g.hitFlash);
  (el('dmg-vignette') as HTMLElement).style.opacity = String(g.damageFlash * 0.9);
  const over = el('game-over');
  over.classList.toggle('hidden', g.alive);
  if (!g.alive) {
    el('go-sub').textContent = `Wave ${g.wave} · ${g.score} points · ${g.kills} kills (${g.headshots} headshots)`;
  }
}

// ---------------------------------------------------------------------------
// Pointer lock and input
// ---------------------------------------------------------------------------

const lockPrompt = el('lock-prompt');
let dragging = false;
/** Set once the browser has actually refused pointer lock. */
let lockUnavailable = false;
document.addEventListener('pointerlockerror', () => {
  lockUnavailable = true;
});

/**
 * Pointer lock is the good path, but some browsers and embedded views refuse
 * it. Rather than trapping the viewer behind a prompt that never clears, fall
 * back to drag-to-look — the level is still fully explorable either way.
 */
function beginLook(): void {
  lockPrompt.classList.add('hidden');
  try {
    const req = renderer.domElement.requestPointerLock() as unknown;
    if (req && typeof (req as Promise<void>).catch === 'function') (req as Promise<void>).catch(() => {});
  } catch {
    lockUnavailable = true;
  }
  updateLookHint();
}

lockPrompt.onclick = beginLook;
renderer.domElement.addEventListener('mousedown', (e) => {
  if (e.button !== 0) return;
  dragging = true;
  const locked = document.pointerLockElement === renderer.domElement;
  if (!locked) beginLook();
  // The click that grabs the pointer should not also fire a shot — unless the
  // browser refuses pointer lock, in which case clicks are all we have.
  if (state.playing && (locked || lockUnavailable)) game.setTrigger(true);
});
addEventListener('mouseup', () => {
  dragging = false;
  game.setTrigger(false);
});
addEventListener('blur', () => {
  dragging = false;
  game.setTrigger(false);
});

document.addEventListener('pointerlockchange', () => {
  const locked = document.pointerLockElement === renderer.domElement;
  if (!locked) {
    player.clearKeys();
    game.setTrigger(false);
  }
  updateLookHint();
});

function updateLookHint(): void {
  const locked = document.pointerLockElement === renderer.domElement;
  el('lock-sub').innerHTML = locked
    ? 'Mouse to look · <b>WASD</b> to move · <b>F</b> to toggle noclip · <b>Esc</b> to release'
    : 'Drag to look · <b>WASD</b> to move · <b>F</b> to toggle noclip';
}

addEventListener('mousemove', (e) => {
  if (document.pointerLockElement === renderer.domElement) player.look(e.movementX, e.movementY);
  else if (dragging) player.look(e.movementX, e.movementY, 0.0032);
});

// Keyboard has to reach the player even without pointer lock.
renderer.domElement.tabIndex = 0;

addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement) return;
  const b = state.current;

  if (e.code === 'Enter') {
    if (state.playing && !game.state.alive) startPlay();
    else togglePlay();
    return;
  }
  if (state.playing) {
    // A cut-down key set while playing: no noclip, no storey isolation.
    switch (e.code) {
      case 'KeyR':
        game.reload();
        return;
      case 'KeyP':
        stopPlay();
        return;
      case 'KeyM':
        togglePlan();
        return;
      case 'Escape':
        if (!el('plan-overlay').classList.contains('hidden')) togglePlan();
        return;
      default:
        player.onKey(e, true);
        return;
    }
  }

  switch (e.code) {
    case 'KeyF':
      player.mode = player.mode === 'walk' ? 'fly' : 'walk';
      player.velocity.set(0, 0, 0);
      renderControls();
      return;
    case 'KeyV':
      state.mode = state.mode === 'compiled' ? 'naive' : 'compiled';
      applyMode();
      renderReport();
      renderControls();
      return;
    case 'KeyN':
      state.showNav = !state.showNav;
      applyToggles();
      renderControls();
      return;
    case 'KeyB':
      state.showBoundaries = !state.showBoundaries;
      applyToggles();
      renderControls();
      return;
    case 'KeyL':
      state.showLabels = !state.showLabels;
      applyToggles();
      renderControls();
      return;
    case 'KeyJ':
      state.showNavmesh = !state.showNavmesh;
      applyToggles();
      renderReport();
      renderControls();
      return;
    case 'KeyK':
      state.showMarkers = !state.showMarkers;
      applyToggles();
      renderControls();
      return;
    case 'KeyX':
      state.showDefects = !state.showDefects;
      applyToggles();
      renderControls();
      return;
    case 'KeyH':
      state.hideRoofs = !state.hideRoofs;
      applyToggles();
      renderControls();
      return;
    case 'KeyG':
      state.breached = !state.breached;
      applyToggles();
      renderControls();
      return;
    case 'KeyP': {
      if (!b) return;
      const ids = b.spec.layers.map((l) => l.id);
      const idx = state.isolatedLayer === null ? 0 : ids.indexOf(state.isolatedLayer) + 1;
      state.isolatedLayer = idx >= ids.length ? null : ids[idx];
      applyToggles();
      renderControls();
      return;
    }
    case 'KeyM':
      togglePlan();
      return;
    case 'KeyT': {
      if (!b || !b.level.markers.length) return;
      state.markerIndex = (state.markerIndex + 1) % b.level.markers.length;
      const m = b.level.markers[state.markerIndex];
      player.teleport(toThree(m.pos[0], m.pos[1], m.pos[2] + 0.1));
      return;
    }
    case 'KeyR':
      if (b) spawnPlayer(b);
      renderControls();
      return;
    case 'KeyO':
      if (b) overview(b);
      renderControls();
      return;
    case 'Escape':
      if (!el('plan-overlay').classList.contains('hidden')) togglePlan();
      else if (document.pointerLockElement !== renderer.domElement) lockPrompt.classList.remove('hidden');
      return;
  }
  player.onKey(e, true);
});

addEventListener('keyup', (e) => player.onKey(e, false));

// ---------------------------------------------------------------------------
// Full-screen plans
// ---------------------------------------------------------------------------

function togglePlan(): void {
  const overlay = el('plan-overlay');
  const showing = overlay.classList.contains('hidden');
  overlay.classList.toggle('hidden', !showing);
  if (!showing) return;
  document.exitPointerLock();
  const b = state.current;
  if (!b) return;
  el('plan-title').textContent = `${b.spec.name} — floor plans`;
  const grid = el('plan-grid');
  grid.innerHTML = '';
  for (const layer of [...b.spec.layers].sort((x, y) => y.z - x.z)) {
    const card = document.createElement('div');
    card.className = 'plan-card';
    card.innerHTML = `<h4>${layer.label ?? layer.id} · z = ${layer.z.toFixed(2)} m · ${layer.spaces.length} spaces</h4>`;
    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 560;
    card.appendChild(canvas);
    grid.appendChild(card);
    drawPlan(canvas.getContext('2d')!, b.level, canvas.width, canvas.height, {
      layerId: layer.id,
      showLabels: true,
      padding: 26,
      background: '#0a0e14',
    });
  }
}

// ---------------------------------------------------------------------------
// Frame loop
// ---------------------------------------------------------------------------

const mmCanvas = el<HTMLCanvasElement>('mm-canvas');
const mmCtx = mmCanvas.getContext('2d')!;
let last = performance.now();
let mmTimer = 0;
let fpsTimer = 0;
let frames = 0;
let fps = 0;

function tick(now: number): void {
  requestAnimationFrame(tick);
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;

  if (state.playing) game.update(dt);
  player.update(dt);
  renderer.render(scene, camera);
  if (state.playing) renderGameHud();

  frames++;
  fpsTimer += dt;
  if (fpsTimer > 0.5) {
    fps = Math.round(frames / fpsTimer);
    frames = 0;
    fpsTimer = 0;
  }

  mmTimer += dt;
  if (mmTimer > 0.06 && state.current) {
    mmTimer = 0;
    const b = state.current;
    const [px, py, pz] = fromThree(player.camera.position);
    const lid = layerAt(b.level, pz - 1.5);
    drawPlan(mmCtx, b.level, mmCanvas.width, mmCanvas.height, {
      layerId: lid,
      player: { x: px, y: py, yaw: player.yaw },
      background: '#0a0e14',
    });
    const layer = b.spec.layers.find((l) => l.id === lid);
    el('mm-layer').textContent = layer?.label ?? lid;
    el('mm-pos').textContent = `${px.toFixed(1)}, ${py.toFixed(1)}, ${pz.toFixed(1)} m`;

    const room = roomAt(b, lid, px, py);
    el('hud').innerHTML =
      `<b>${room ?? '—'}</b> · ${player.mode === 'fly' ? '<span class="fly">noclip</span>' : 'walking'}` +
      (state.breached ? ' · <span class="fly">breached</span>' : '') +
      (state.isolatedLayer ? ` · storey <b>${state.isolatedLayer}</b>` : '') +
      ` · ${fps} fps · ${player.colliderCount} colliders`;
  }
}

function roomAt(b: Built, layerId: string, x: number, y: number): string | null {
  const g = b.spec.grid ?? 1;
  const occ = b.level.occupancy.get(layerId);
  if (!occ) return null;
  const id = occ.get(`${Math.floor(x / g)},${Math.floor(y / g)}`);
  if (!id) return null;
  const layer = b.spec.layers.find((l) => l.id === layerId);
  return layer?.spaces.find((s) => s.id === id)?.label ?? id;
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

renderRail();
renderControls();
requestAnimationFrame(tick);

// A handle for poking at a build from the console.
(window as unknown as { levelspec: unknown }).levelspec = { state, player, game, cache, SPECS, scene, camera, startPlay, stopPlay };

void (async () => {
  await loadLevel(SPECS[0]);
  // Warm the rest in the background so the rail chips reflect a real build,
  // not a cached report file.
  for (const spec of SPECS.slice(1)) {
    await new Promise((r) => setTimeout(r, 60));
    await build(spec);
    renderRail();
  }
})();
